import * as React from 'react';
import {View} from 'react-native';
import {isEmpty, noop} from 'lodash';

import FormattedNumber from '/component/base/FormattedNumber/FormattedNumber';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import Gutter from '/component/layout/Gutter';
import {UserProfileModal} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import {
  generateCallback,
  generateComponentCallback,
} from '/util/component.util';
import {getDateUtilDefaultInstance} from '/util/date.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './OrderHistoryReportContent.style';
interface Order {
  id: string;
  orderNumber: string;
  orderedOn: string;
  price: {
    total: number;
    currencyCode: string;
  };
}
interface RecordData {
  recentOrders?: Order[];
}
interface Props {
  tt: (
    key: string,
    fallback: string | (() => React.ReactNode),
  ) => string | React.ReactNode;
  recordData: RecordData;
  onOrderPress: (params: {orderId: string}) => void;
}

export const OrderHistoryReportContent: React.FC<Props> = ({
  tt,
  recordData = {},
  onOrderPress = noop,
}) => {
  const {recentOrders = []} = recordData;

  const renderEmptyOrderRow = generateComponentCallback({
    component: (
      <View
        {...testProperties(UserProfileModal.orderHistoryNoItem)}
        style={styles.order}>
        <View style={styles.orderLine}>
          <View style={styles.orderDate}>
            <Text>{tt('noOrderText', () => 'No Orders Yet')}</Text>
          </View>
          <View style={styles.orderAmount}>
            <Text style={styles.orderAmountEmptyText}>--</Text>
          </View>
        </View>
      </View>
    ),
  });

  const renderOrderRow = (order: Order, index: number) => {
    const {
      id,
      orderNumber,
      orderedOn,
      price: {total, currencyCode},
    } = order;

    return (
      <TouchableOpacity
        {...testProperties(UserProfileModal.orderHistoryItem, true)}
        key={index}
        style={styles.order}
        onPress={generateCallback({func: onOrderPress, params: {orderId: id}})}>
        <View style={styles.orderLine}>
          <View style={styles.orderColumn1}>
            <View style={styles.orderDate}>
              <Text testID={UserProfileModal.orderHistoryDate}>
                {getDateUtilDefaultInstance().formatDate(
                  orderedOn,
                  'MMMM d, yyyy',
                )}
              </Text>
            </View>
            {tt(
              'orderNumber',
              generateComponentCallback({
                component: (
                  <Text
                    style={styles.orderNumberContainer}
                    variant="caption"
                    emphasis="disabled"
                    testID={UserProfileModal.orderHistoryNumber}>
                    Order #:
                    <Text
                      style={styles.orderNumber}
                      variant="caption"
                      emphasis="disabled">
                      {orderNumber}
                    </Text>
                  </Text>
                ),
              }),
            )}
          </View>
          <View style={styles.orderColumn2}>
            <View style={styles.orderAmount}>
              <FormattedNumber
                testID={UserProfileModal.orderHistoryAmount}
                value={total}
                currencyCode={currencyCode}
                decimalPlaces={2}
                boldText={true}
              />
            </View>
          </View>
        </View>
        <Icon name="chevron-right" style={styles.arrowIcon} />
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.base}>
      <View style={styles.header}>
        <Gutter>
          <Text variant="heading" style={styles.headerText}>
            {tt('heading', 'Order History')}
          </Text>
        </Gutter>
      </View>
      <Gutter>
        {isEmpty(recentOrders)
          ? renderEmptyOrderRow()
          : recentOrders.map(renderOrderRow)}
      </Gutter>
    </View>
  );
};

export default withTranslation('reportCardContent.orderHistory')(
  OrderHistoryReportContent,
);
